<script setup>
import {computed, getCurrentInstance, onMounted, ref, watch} from 'vue'
import helpers from '@/lib/helpers'
import NotificationRules from './notification-rules.vue'
import NotificationConditionPicker from './notification-condition-picker.vue'

const {proxy} = getCurrentInstance()

const props = defineProps({
  // model short-name: Order, Bid, PO, BidLine, Item, ...
  type: {type: String, required: true},
  id: {type: [Number, String], required: true},
  gli: {type: String, default: ''},
  label: {type: String, default: ''},
})

const path = `/api/user/self/notification/rule/for/${props.type}/${props.id}`

const rules = ref([])
const triggers = ref([])
const about = ref(null)
const pick = ref('')
const once = ref(true)
const focus = ref(true)
const err = ref('')
const busy = ref(false)

const title = computed(() =>
  'Notify me — ' + (props.label || props.gli || `${props.type} ${props.id}`))

const addable = computed(() => {
  const have = new Set(rules.value.map(r => r.action))
  return triggers.value.filter(t => !have.has(t.key)).map(t => ({value: t.key, text: t.label}))
})

// condition for the trigger about to be added - reset whenever the pick changes
const pickTrigger = computed(() => triggers.value.find(t => t.key === pick.value) || {})
const addCondition = ref('_default')
watch(pick, () => {
  addCondition.value = '_default'
})

function ruleTrigger(rule) {
  return triggers.value.find(t => t.key === rule.action) || {}
}

// inline condition editor for whichever rule is currently selected
const selectedRuleId = ref(null)
const condTrigger = ref({})
const condValue = ref('_default')
const condBusy = ref(false)

function selectRule(rule) {
  if (selectedRuleId.value === rule.id) {
    selectedRuleId.value = null
    return
  }
  condTrigger.value = ruleTrigger(rule)
  condValue.value = rule.condition || '_default'
  selectedRuleId.value = rule.id
}

async function saveCondition(rule) {
  if (condBusy.value) return
  condBusy.value = true
  try {
    await helpers.asyncAction(rule._links.update_url.href, 'PUT', {
      noload: 'nwatch', data: {action: rule.action, condition: condValue.value},
    })
    rule.condition = condValue.value
    helpers.toast('Condition updated')
    selectedRuleId.value = null
  } catch (e) {
    helpers.alert(e.message || 'Could not update condition')
  } finally {
    condBusy.value = false
  }
}

async function load() {
  try {
    const r = await helpers.asyncAction(path, 'GET', {noload: 'nwatch', quiet: true})
    about.value = r.about
    rules.value = r.rules || []
    triggers.value = r.triggers || []
    err.value = ''
  } catch (e) {
    err.value = e.message || 'Could not load'
  }
}

async function add() {
  if (!pick.value || busy.value) return
  busy.value = true
  try {
    const data = {action: pick.value, condition: addCondition.value}
    if (once.value) data.fire_limit = 1
    if (focus.value) data.focused = true
    await helpers.asyncAction(path, 'POST', {noload: 'nwatch', data})
    pick.value = ''
    addCondition.value = '_default'
    once.value = true
    focus.value = true
    await load()
    helpers.toast('Notification added')
  } catch (e) {
    helpers.alert(e.message || 'Could not add')
  } finally {
    busy.value = false
  }
}

async function toggle(rule) {
  try {
    await helpers.asyncAction(rule._links.update_url.href, 'PUT', {
      noload: 'nwatch', quiet: true,
      data: {action: rule.action, enabled: !rule.enabled},
    })
    rule.enabled = !rule.enabled
  } catch (e) {
    helpers.alert(e.message || 'Could not update')
  }
}

async function remove(rule) {
  if (!await helpers.confirm('Remove this notification?')) return
  try {
    await helpers.asyncAction(rule._links.delete_url.href, 'DELETE', {noload: 'nwatch'})
    await load()
  } catch (e) {
    helpers.alert(e.message || 'Could not remove')
  }
}

function manageAll() {
  proxy.$bvModal.hide('modal')
  proxy.$dialog.show(NotificationRules)
}

onMounted(load)
</script>

<template>
  <modal :title="title" height="420px" :busy="busy">
    <div class="px-2 pt-2">
      <b-alert :show="!!err" variant="danger" class="py-1 small">{{ err }}</b-alert>

      <div v-if="!rules.length" class="text-muted small mb-2">
        You're not watching this {{ type.toLowerCase() }} yet.
      </div>

      <b-list-group flush class="mb-2">
        <template v-for="r in rules">
          <b-list-group-item :key="r.id" class="d-flex align-items-center px-1 py-2">
            <b-form-checkbox
              switch
              size="sm"
              class="mr-2"
              :checked="r.enabled"
              @change="toggle(r)"/>
            <div class="flex-fill min-w-0 cursor-pointer" @click="selectRule(r)">
              <span class="text-truncate d-block" :class="{'text-muted': !r.enabled}">
                {{ r.name || r.trigger_label }}
              </span>
              <span v-if="r.name" class="text-muted small">{{ r.trigger_label }}</span>
            </div>
            <font-awesome-icon v-if="r.focused_effective" :icon="['fas', 'bell']" size="xs"
                               class="text-muted mr-1" title="Focused"/>
            <b-badge v-if="r.fire_limit" variant="light" class="border text-muted mr-1"
                     title="Auto-deletes after this many deliveries">
              {{ Math.max(0, r.fire_limit - r.match_count) }} left
            </b-badge>
            <b-badge v-else-if="r.fire_count" variant="light" class="border text-muted mr-1">{{ r.fire_count }}</b-badge>
            <b-button variant="link" size="sm" class="px-1" :class="selectedRuleId === r.id ? 'text-primary' : 'text-muted'"
                      title="Conditions" @click="selectRule(r)">
              <font-awesome-icon :icon="['fas', 'filter']"/>
            </b-button>
            <b-button variant="link" size="sm" class="text-danger px-1" @click="remove(r)">
              <font-awesome-icon :icon="['far', 'trash-can']"/>
            </b-button>
          </b-list-group-item>

          <b-list-group-item v-if="selectedRuleId === r.id" :key="r.id + '-cond'" class="px-2 py-2 bg-light">
            <notification-condition-picker :trigger="condTrigger" v-model="condValue"/>

            <div class="d-flex justify-content-end mt-2">
              <b-button variant="outline-secondary" size="sm" class="px-3 mr-2" @click="selectedRuleId = null">
                Cancel
              </b-button>
              <b-button variant="primary" size="sm" class="px-3" :disabled="condBusy" @click="saveCondition(r)">
                Save
              </b-button>
            </div>
          </b-list-group-item>
        </template>
      </b-list-group>

      <b-form-select
        v-model="pick"
        size="sm"
        class="mb-2"
        :options="addable"
        :disabled="!addable.length">
        <template #first>
          <b-form-select-option :value="''" disabled>
            {{ addable.length ? 'Add a notification…' : 'Nothing left to add' }}
          </b-form-select-option>
        </template>
      </b-form-select>

      <template v-if="pick">
        <notification-condition-picker :trigger="pickTrigger" v-model="addCondition"/>

        <hr />

        <b-form-checkbox v-model="once" size="sm" class="mt-1 text-muted">
          Just once (auto-delete after it fires)
        </b-form-checkbox>
        <b-form-checkbox v-model="focus" size="sm" class="text-muted mb-2">
          Focused
        </b-form-checkbox>

        <b-button variant="primary" size="sm" class="px-3" block :disabled="busy" @click="add">Add</b-button>
      </template>
    </div>

    <template #footer>
      <b-button variant="link" size="sm" class="text-secondary mr-auto" @click="manageAll">
        Manage all notifications&hellip;
      </b-button>
    </template>
  </modal>
</template>

<style scoped>
.min-w-0 {
  min-width: 0;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
